import React, {
    Children,
    isValidElement,
    type ReactElement,
    type ReactNode,
} from 'react';

import type {
    RadioControlGroupOptionProps,
    RadioControlGroupValue,
} from '../RadioControlGroupOption';

import { NestedControl } from './NestedControl';
import { RadioOption } from './RadioOption';

const isSingularElement = (child: ReactNode): child is ReactElement =>
    !!child && React.Children.count(child) === 1;

export const buildOptionRows = ({
    children,
    disabled,
    selectedValue,
}: {
    children: ReactNode;
    disabled?: boolean;
    selectedValue: RadioControlGroupValue;
}): [radioOptions: ReactNode[], nestedControls: ReactNode[]] => {
    const radioOptions: ReactNode[] = [];
    const nestedControls: ReactNode[] = [];

    Children.forEach(children, (child, index) => {
        if (!isValidElement<RadioControlGroupOptionProps>(child)) {
            return;
        }

        // CSS grids start at 1, first row is the ControlGroup label so start controls at row 2
        const $row = index + 2;

        const { children: nestedChild, ...props } = child.props;
        const $hasControl = isSingularElement(nestedChild);

        radioOptions.push(
            <RadioOption
                key={`radio-${child.props.value}`}
                {...props}
                error={!!props.error && child.props.value === selectedValue}
                $hasControl={$hasControl}
                $row={$row}
            />
        );

        if ($hasControl) {
            nestedControls.push(
                <NestedControl
                    key={`control-${child.props.value}`}
                    {...props}
                    $row={$row}
                    child={nestedChild}
                    disabled={disabled}
                    selectedValue={selectedValue}
                />
            );
        }
    });

    return [radioOptions, nestedControls];
};
